import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "src/components/Dialog";
import Box from "@material-ui/core/Box";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import ZonedKeyboardDatePicker from "src/components/ZonedKeyboardDatePicker";

import NumberField from "src/components/NumberField";
import LoadableButton from "src/components/LoadableButton";
import { addReconciliation } from "src/apiService/modules/reconciliations";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import type { Reconciliation } from "src/interfaces/reconciliation.interface";
import {
  calendarYmdInTimeZone,
  startOfCalendarDayContainingInstant,
} from "src/utils/timezone";

const useStyles = makeStyles({
  content: {
    "&:first-child": {
      paddingTop: 0,
    },
  },
});

interface ContentProps {
  bankAccountId: string;
  onClose: () => void;
  account: string;
  onReconciliationUpdated: (reconciliation: Reconciliation) => void;
}
function Content({
  bankAccountId,
  onClose,
  account,
  onReconciliationUpdated,
}: ContentProps) {
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const classes = useStyles();
  const formik = useFormik({
    initialValues: {
      date: null,
      balance: null,
    } as { date: Date | null; balance: number | null },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.balance && values.balance !== 0)
        errors.balance = "Required field";
      if (!values.date) errors.date = "Required field";
      return errors;
    },
    onSubmit: async (values) => {
      if ((!values.balance && values.balance !== 0) || !values.date) return;
      const dateYmd = calendarYmdInTimeZone(values.date, timeZone);
      const created = (await addReconciliation(
        bankAccountId,
        values.balance,
        dateYmd
      )) as { reconciliation?: Reconciliation };
      if (created.reconciliation) {
        onReconciliationUpdated(created.reconciliation);
      }
      onClose();
    },
    validateOnBlur: false,
    validateOnChange: false,
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <DialogContent className={classes.content}>
        <TextField
          value={account}
          inputProps={{ readOnly: true }}
          margin="dense"
          variant="outlined"
          fullWidth
          label="Account"
        />
        <NumberField
          allowNegative
          autoComplete="off"
          margin="dense"
          fullWidth
          required
          label="Beginning Balance"
          value={formik.values.balance ?? ""}
          variant="outlined"
          onChange={formik.handleChange}
          name="balance"
          helperText={formik.errors?.balance || undefined}
          error={!!formik.errors?.balance}
          disabled={formik.isSubmitting}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
        <Box pt={1}>
          <ZonedKeyboardDatePicker
            fullWidth
            variant="inline"
            inputVariant="outlined"
            disableToolbar
            format="MM/dd/yyyy"
            label="Beginning Date"
            required
            autoOk
            onChange={(d) => {
              if (d) {
                const date = startOfCalendarDayContainingInstant(d, timeZone);
                formik.setValues((v) => ({
                  ...v,
                  date,
                }));
              } else {
                formik.setValues((v) => ({
                  ...v,
                  date: null,
                }));
              }
            }}
            value={formik.values.date || null}
            helperText={formik.errors?.date || undefined}
            error={!!formik.errors?.date}
            disabled={formik.isSubmitting}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <LoadableButton
          color="primary"
          variant="contained"
          type="submit"
          loading={formik.isSubmitting}
        >
          Add
        </LoadableButton>
      </DialogActions>
    </form>
  );
}

function BeginningBalanceDialog({
  open,
  onClose,
  bankAccountId,
  account,
  onReconciliationUpdated,
}: ContentProps & { open: boolean }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Reconciliation</DialogTitle>
      <Content
        bankAccountId={bankAccountId}
        onClose={onClose}
        account={account}
        onReconciliationUpdated={onReconciliationUpdated}
      />
    </Dialog>
  );
}

export default BeginningBalanceDialog;
